import { authRequest, authRequestDownload } from './client';
import {
  type AccountantAvailableDatesResponse,
  type AccountantTotalsResponse,
  type GetAccountantSnapshotParams,
  type ListAccountantSnapshotResponse,
} from './types';

// Task 4: read-side client for the Accountant feature — mirrors the
// `apps/backoffice/src/api/ledgers.ts` pattern exactly. FINANCE_VIEWER-gated
// like `/v1/ledgers`, same auth/refresh handling via `authRequest`.
//
// `GET /v1/accountant/export` is FINANCE_VIEWER-gated exactly like the other
// three routes here — the backend's JWT strategy only ever extracts the
// token from an `Authorization: Bearer` header (no cookie, no query-param
// fallback; see `services/backoffice-api/src/auth/jwt.strategy.ts`), so a
// plain `<a href>` navigation (the plan's original "browser-native download"
// wording) would always 401: the browser never attaches the app's
// localStorage-held token to a bare link click. `exportAccountsCsv` below
// instead fetches the CSV through `authRequestDownload` (the same
// Bearer-attach + refresh-and-retry plumbing every other call in this file
// uses) and triggers the save via a temporary object-URL anchor — the button
// in `_app.accounts.tsx` calls this on click rather than rendering a static
// href.

export function getTotals(date?: string): Promise<AccountantTotalsResponse> {
  const query = new URLSearchParams();
  if (date !== undefined) query.set('date', date);
  const qs = query.toString();
  return authRequest<AccountantTotalsResponse>(`/accountant/totals${qs ? `?${qs}` : ''}`);
}

export function getSnapshot(params: GetAccountantSnapshotParams): Promise<ListAccountantSnapshotResponse> {
  const query = new URLSearchParams();
  query.set('date', params.date);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  return authRequest<ListAccountantSnapshotResponse>(`/accountant/snapshot?${query.toString()}`);
}

export function getAvailableDates(): Promise<AccountantAvailableDatesResponse> {
  return authRequest<AccountantAvailableDatesResponse>('/accountant/available-dates');
}

/**
 * Fetches `GET /v1/accountant/export?date=` as a Bearer-authenticated blob
 * and triggers a browser save via a temporary object-URL anchor — see the
 * file-header doc comment for why this isn't a plain `<a href>` link.
 * Filename comes from the response's `Content-Disposition` header when
 * present, falling back to `accounts-<date>.csv` (matching the backend's own
 * naming) otherwise.
 */
export async function exportAccountsCsv(date: string): Promise<void> {
  const query = new URLSearchParams({ date });
  const { blob, filename } = await authRequestDownload(`/accountant/export?${query.toString()}`);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? `accounts-${date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
